// Tracks which revision the app is viewing: the working copy by default, or a
// thread's branch while a thread is open. Kept as its own store so the openapi
// client and every revision-aware read stay in sync with what the user sees.
import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { threadRevisionContext } from "@/lib/openapi/thread-revision-context";

export const useReleaseStore = defineStore("release", () => {
    // Tracks which thread is open in the drawer (for the ?thread= URL param).
    // When set, the stek is viewed at the thread's branch: Phase 5 of
    // thread-branch-wiring.md made the backend resolve a `thread/<id>` revision
    // (branch content for forked domains + master fallback for untouched ones),
    // so revision-aware reads can safely point at the branch.
    const activeThreadId = ref<string | null>(null);

    const activeRevision = computed(() =>
        activeThreadId.value ? `thread/${activeThreadId.value}` : null
    );

    // Drives the "viewing thread branch" banner.
    const viewingThreadBranch = computed(() => activeThreadId.value !== null);

    // Keep the leaf holder the openapi client reads in sync with the active
    // revision (read-revision-as-context). Only thread/<id> is a read ambient;
    // the working copy clears it so reads resolve at master. immediate so a
    // deep-linked ?thread= (store hydrated before the first request) is covered.
    watch(
        activeRevision,
        (rev) => {
            threadRevisionContext.current = rev?.startsWith("thread/")
                ? rev
                : null;
        },
        { immediate: true }
    );

    const setOpenThreadId = (threadId: string) => {
        activeThreadId.value = threadId;
    };

    const clearOpenThreadId = () => {
        activeThreadId.value = null;
    };

    /**
     * Reset revision state when switching to a different stek, so a thread open
     * in the previous stek does not carry over into the new one.
     */
    const resetForNewStek = () => {
        activeThreadId.value = null;
    };

    return {
        activeThreadId,
        activeRevision,
        viewingThreadBranch,
        setOpenThreadId,
        clearOpenThreadId,
        resetForNewStek,
    };
});

export const useReleaseManager = () => storeToRefs(useReleaseStore());
